"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, ArrowRight, BookOpen, ShieldAlert } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function SafetyReview() {
  const shouldReduceMotion = useReducedMotion();
  const easeCurve = [0.16, 1, 0.3, 1] as const;
  const narrativeBlocks = [{
    id: "doc-source",
    tag: "DOC REF // 01",
    text: "Safety information should come from the documentation for the exact mechanical bull you are considering. Different machines can use different controls, emergency-stop arrangements, rider restrictions and operating procedures."
  }, {
    id: "pre-op",
    tag: "PROC REF // 02",
    text: "Before operating the equipment, review the available manufacturer or product documentation and understand the machine's emergency controls, rider restrictions, safety-area requirements and maintenance guidance."
  }, {
    id: "no-assumptions",
    tag: "CRITICAL // 03",
    text: "Do not assume that one safety procedure applies to every mechanical bull."
  }];
  return <section id="exactmachinesafetyreview" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border selection:bg-primary selection:text-primary-foreground relative">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Section Header */}
        <div className="mb-10 md:mb-12">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-muted border border-border text-xs uppercase tracking-widest font-mono text-muted-foreground mb-4">
            <ShieldAlert className="size-3.5 text-primary" />
            <span>Industrial Safety Advisory</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-serif font-bold uppercase tracking-tight text-foreground">
            Review the Safety Information for the Exact Machine
          </h2>
        </div>

        {/* Advisory Enclosure / Rating Plate Panel */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: shouldReduceMotion ? 0 : 0.3,
        ease: easeCurve
      }} className="relative bg-card border-2 border-border shadow-xs">
          {/* Machine plate corner rivets */}
          <div className="absolute top-2 left-2 size-2 bg-border border border-muted-foreground/40 rounded-full" />
          <div className="absolute top-2 right-2 size-2 bg-border border border-muted-foreground/40 rounded-full" />
          <div className="absolute bottom-2 left-2 size-2 bg-border border border-muted-foreground/40 rounded-full" />
          <div className="absolute bottom-2 right-2 size-2 bg-border border border-muted-foreground/40 rounded-full" />

          {/* Metal Rating Plate Header Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border bg-muted/60 px-6 py-3.5 text-xs font-mono uppercase tracking-wider text-muted-foreground">
            <div className="flex items-center gap-2">
              <span className="inline-block size-2 bg-primary" />
              <span className="font-semibold text-foreground">
                Technical Specification Notice
              </span>
            </div>
            <div className="flex items-center gap-4 text-[11px]">
              <span>SPEC ID: 884-SAF</span>
              <span className="hidden sm:inline">SERIAL MATCH MANDATORY</span>
            </div>
          </div>

          {/* Narrative Content Stack */}
          <div className="p-6 md:p-10 space-y-6">
            <div className="grid grid-cols-1 gap-5">
              {narrativeBlocks.map((block, index) => <div key={block.id} data-index={index} className="group relative border border-border bg-background p-5 md:p-6 transition-[border-color,background-color] duration-150 hover:border-primary/50">
                  <div className="flex items-center gap-2 text-xs font-mono font-medium text-muted-foreground mb-2">
                    <span className="text-primary font-bold">{block.tag}</span>
                    <span className="text-border">|</span>
                    <span className="uppercase text-[11px] tracking-wider">
                      Operating Protocol
                    </span>
                  </div>
                  <p className="font-default text-base md:text-lg leading-relaxed text-foreground">
                    {block.text}
                  </p>
                </div>)}
            </div>

            {/* Industrial Warning Callout Banner */}
            <div className="flex items-start gap-3 bg-muted/50 border-l-4 border-primary p-4 text-xs md:text-sm text-muted-foreground font-mono">
              <AlertTriangle className="size-4 text-primary shrink-0 mt-0.5" />
              <p>
                Notice: Specific operating tolerances, electrical connection requirements, safety perimeter dimensions, and emergency cutout switch responses vary by chassis and controller configuration.
              </p>
            </div>

            {/* Action Row */}
            <div className="pt-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-border">
              <div className="text-xs font-mono text-muted-foreground flex items-center gap-1.5">
                <BookOpen className="size-3.5" />
                <span>Reference internal standard operating documents</span>
              </div>
              <Button asChild size="lg" className="font-mono text-xs uppercase tracking-wider font-semibold active:scale-[0.99] transition-[background-color,border-color,box-shadow] duration-180">
                <Link to="/SetupSafetyAndMaintenancePage" className="inline-flex items-center gap-2">
                  <span>Read Setup &amp; Safety</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}